import { ResponseEvents, SystemEvents } from '@luxuryescapes/contract-search'
import { disconnectSearchSocket, getSearchSocket } from 'search/initialiseSearchSocket'
import { sum } from 'lib/array/arrayUtils'

// --- Types & Static Content ---

interface SearchSocketListener {
  event: SystemEvents | ResponseEvents
  handler: (...args: Array<unknown>) => void
}

interface SearchSocketListenerEntry {
  searchSocketListeners: Array<SearchSocketListener>
  stateListeners: Array<string>
}

const searchSocketListenersMap: Map<string, SearchSocketListenerEntry> = new Map()

/**
 * Register a socket event listener for a given offer list key.
 */
export function addSearchSocketListener({
  eventGroupKey,
  event,
  handler,
}: {
  eventGroupKey: string
  event: SystemEvents | ResponseEvents
  handler: (...args: Array<unknown>) => void
}) {
  ensureEntryExists({ eventGroupKey })
  const entry = searchSocketListenersMap.get(eventGroupKey)
  if (!entry) return
  entry.searchSocketListeners.push({ event, handler })
  getSearchSocket().on(event, handler)
}

/**
 * Remove all socket listeners for a given offer list key and clean up entry.
 */
export function removeAllSearchSocketListenersAndSafelyDisconnect({
  eventGroupKey,
}: {
  eventGroupKey: string
}) {
  const entry = searchSocketListenersMap.get(eventGroupKey)
  if (!entry?.searchSocketListeners.length) return

  const searchSocket = getSearchSocket()
  for (const { event, handler } of entry.searchSocketListeners) searchSocket.off(event, handler)

  entry.searchSocketListeners = []
  removeKeyIfNoListeners({ eventGroupKey })

  if (totalSocketListenerCount() === 0) disconnectSearchSocket()
}

/**
 * Remove the eventGroupKey entry if both searchSocketListeners and stateListeners arrays are empty.
 */
export function removeKeyIfNoListeners({
  eventGroupKey,
}: {
  eventGroupKey: string
}) {
  const entry = searchSocketListenersMap.get(eventGroupKey)
  if (!entry) return

  if (entry.searchSocketListeners.length === 0 && entry.stateListeners.length === 0) {
    searchSocketListenersMap.delete(eventGroupKey)
  }
}

/**
 * Register a state listener (e.g., UI component) for a given offer list key.
 */
export function addStateListener({
  eventGroupKey,
  listenerId,
}: {
  eventGroupKey: string
  listenerId: string
}) {
  ensureEntryExists({ eventGroupKey })
  const entry = searchSocketListenersMap.get(eventGroupKey)
  if (!entry) return

  if (!entry.stateListeners.includes(listenerId)) {
    entry.stateListeners = [...entry.stateListeners, listenerId]
  }
}

/**
 * Remove a state listener for a given offer list key.
 * If no listeners remain, pause the search and clean up socket listeners.
 */
export function removeStateListenerAndMaybePause({
  eventGroupKey,
  listenerId,
  searchId,
  streamEnded,
}: {
  eventGroupKey: string
  listenerId: string
  searchId?: string
  streamEnded?: boolean
}) {
  const entry = searchSocketListenersMap.get(eventGroupKey)
  if (!entry || !searchId) return

  entry.stateListeners = entry.stateListeners.filter(id => id !== listenerId)
  if (entry.stateListeners.length > 0) return

  if (!streamEnded) getSearchSocket().emit('search:request:pause', { searchId })
  removeAllSearchSocketListenersAndSafelyDisconnect({ eventGroupKey })
}

/**
 * Get all state listener IDs for a given offer list key.
 */
export function getStateListeners(eventGroupKey: string): Array<string> {
  return searchSocketListenersMap.get(eventGroupKey)?.stateListeners ?? []
}

// --- Helpers & Internal ---

function ensureEntryExists({ eventGroupKey }: { eventGroupKey: string }) {
  if (!searchSocketListenersMap.has(eventGroupKey)) {
    searchSocketListenersMap.set(eventGroupKey, {
      searchSocketListeners: [],
      stateListeners: [],
    })
  }
}

function totalSocketListenerCount(): number {
  return sum([...searchSocketListenersMap.values()], entry => entry.searchSocketListeners.length)
}
